import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { CRITERIA } from "@/data/criteria";
import { rankBrokers } from "@/lib/matching";
import { Reveal, SplitLines } from "@/components/motion/Reveal";
import { BrokerMark, MatchRing, Eyebrow, Tag } from "@/components/shared/Primitives";
import { useBrokers } from "@/context/BrokersContext";

const benefitFor = (b, ids) => {
  if (ids.includes("swapFree") && b.swapFree) return b.swapFreeNote;
  if (ids.includes("bonus") && b.depositBonus) return b.depositBonus;
  if (ids.includes("rawSpreads")) return `${b.spreadModel} · EUR/USD ${b.eurUsdSpread.toFixed(1)} pips · $${b.commission.toFixed(2)}/lot`;
  if (ids.includes("fastFunding")) return `Withdrawals: ${b.withdrawalTime}`;
  return b.highlight;
};

export const SelectionEngine = () => {
  const { brokers } = useBrokers();
  const [sel, setSel] = useState(["rawSpreads", "fastFunding"]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const results = useMemo(() => rankBrokers({ criteria: Object.fromEntries(sel.map((id) => [id, "preference"])) }, 3, brokers), [sel, brokers]);

  return (
    <section className="section bg-white" data-testid="selection-engine">
      <div className="container-x">
        <Reveal>
          <Eyebrow className="mb-4">The broker selection engine</Eyebrow>
          <SplitLines as="h2" inView lines={["One platform.", "The broker that fits you."]} className="t-h2" lineClass="" />
          <p className="mt-5 max-w-xl t-lead">Select what matters. Scores recompute instantly from an auditable rules-based methodology — affiliate payouts never touch the ranking.</p>
        </Reveal>

        <Reveal delay={0.1} className="mt-12 grid grid-cols-2 gap-px bg-[var(--line)] border border-[var(--line)] md:grid-cols-5">
          {CRITERIA.map((c, i) => {
            const on = sel.includes(c.id);
            const Icon = c.icon;
            return (
              <button key={c.id} onClick={() => toggle(c.id)} data-on={on} data-testid={`criterion-${c.id}`}
                className={`card card-tap flex min-h-[188px] flex-col border-0 p-5 text-left lg:p-6 ${i === CRITERIA.length - 1 ? "col-span-2 md:col-span-1" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <span className={`eyebrow ${on ? "text-[var(--cobalt)]" : ""}`}>0{i + 1} · {c.short}</span>
                  <Icon size={16} strokeWidth={1.6} className={on ? "text-[var(--cobalt)]" : "text-[var(--ink-4)]"} />
                </div>
                <p className={`mt-auto pt-10 card-value ${on ? "text-[var(--cobalt)]" : ""}`}>{c.label}</p>
                <p className="mt-2 font-mono text-[11px] text-[var(--ink-3)]">{c.metric}</p>
              </button>
            );
          })}
        </Reveal>

        <div className="mt-24 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <Eyebrow className="mb-4">Featured broker recommendations</Eyebrow>
            <h3 className="t-h3">Top matches for your selection</h3>
          </Reveal>
          <Reveal delay={0.1}><Link to="/brokers/finder" className="btn-primary" data-testid="engine-open-finder">Open the full matching experience <ArrowRight size={16} /></Link></Reveal>
        </div>

        <div className="mt-8 border-t border-[var(--line)]">
          <AnimatePresence initial={false} mode="popLayout">
            {results.map(({ broker: b, score, entity }, i) => (
              <motion.div key={b.slug} layout initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
                className="row-line row-hover grid grid-cols-12 items-center gap-4 py-6 lg:py-7" data-testid={`featured-broker-${b.slug}`}>
                <div className="col-span-12 flex items-center gap-4 lg:col-span-4">
                  <span className="font-mono text-xs text-[var(--ink-4)] w-5">0{i + 1}</span>
                  <BrokerMark broker={b} size={44} />
                  <div>
                    <p className="t-h5">{b.name}</p>
                    <p className="font-mono text-[11px] text-[var(--ink-3)]">{entity.regulator} · {entity.jurisdiction}</p>
                  </div>
                </div>
                <div className="col-span-8 lg:col-span-4">
                  <p className="text-[15px] text-[var(--ink-2)] leading-[1.5]">{benefitFor(b, sel)}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Tag>Min ${b.minDeposit}</Tag><Tag>{b.platforms.slice(0, 2).join(" · ")}</Tag>{b.swapFree && <Tag tone="blue">Swap-free</Tag>}
                  </div>
                </div>
                <div className="col-span-4 flex items-center justify-end gap-6 lg:col-span-4">
                  <MatchRing value={score} size={56} />
                  <div className="hidden sm:flex items-center gap-2">
                    <Link to={`/brokers/${b.slug}`} className="btn-outline btn-sm" data-testid={`featured-profile-${b.slug}`}>Profile</Link>
                    <a href={b.referral} target="_blank" rel="noreferrer sponsored" className="link-arrow text-[13px]" data-testid={`featured-visit-${b.slug}`}>Visit <ArrowUpRight size={14} /></a>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        <p className="mt-4 font-mono text-[11px] text-[var(--ink-4)]">Scores reflect your selected criteria only. "Visit" opens an external broker registration page via referral link.</p>
      </div>
    </section>
  );
};
